import React, { useState, useLayoutEffect, useEffect, useRef } from 'react';

// ─────────────────────────────────────────────────────────────────────────────
// Module tab bar with a sliding brand-orange pill — shared by Tasks, Services,
// Clients, CRM and Finance so every module's tabs look and move the same way.
//
//   <PillTabs
//     tabs={[{ key, label, count?, alert? }]}   // count → badge; alert → red tab (e.g. Overdue)
//     activeKey={...} onChange={key => ...} T={T} darkMode={darkMode} />
//
// The active tab is a filled pill (orange; red for an `alert` tab). Switching tabs slides
// the pill to the new tab with one smooth ease-out (no bounce); the pill also resizes to the
// new label. First paint and window/label resizes snap without animating, and users with
// "reduce motion" turned on get an instant switch.
// ─────────────────────────────────────────────────────────────────────────────

const BRAND = '#f97316';
const ALERT = '#dc2626';
const EASE = 'cubic-bezier(0.4, 0, 0.2, 1)';
const DURATION = 260; // ms

const prefersReducedMotion = () => {
  try { return window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch { return false; }
};

export default function PillTabs({ tabs, activeKey, onChange, T, darkMode = false }) {
  const trackRef = useRef(null);
  const btnRefs = useRef({});
  const [pill, setPill] = useState(null);    // { left, width, color }
  const [animate, setAnimate] = useState(false);
  const lastKey = useRef(activeKey);
  // Hover shade lives in state (not painted onto the DOM node), so the moment a hovered tab
  // becomes active its shade is gone and can't cover the orange pill / white label.
  const [hoverKey, setHoverKey] = useState(null);

  const active = tabs.find(t => t.key === activeKey);
  const pillColor = active?.alert ? ALERT : BRAND;

  // Signature of everything that can change tab widths (labels / counts).
  const sig = tabs.map(t => `${t.key}|${t.label}|${t.count ?? ''}`).join('~');

  const pillRef = useRef(null);
  const measure = (withAnimation) => {
    const el = btnRefs.current[activeKey];
    if (!el) { pillRef.current = null; setPill(null); return; }
    const g = { left: el.offsetLeft, top: el.offsetTop, width: el.offsetWidth, height: el.offsetHeight };
    const p = pillRef.current;
    // Nothing moved (e.g. ResizeObserver's first call, fonts already loaded) — leave the pill,
    // and any slide in progress, alone.
    if (p && p.left === g.left && p.top === g.top && p.width === g.width && p.height === g.height) return;
    pillRef.current = g;
    setAnimate(withAnimation && !prefersReducedMotion());
    setPill(g);
  };

  // Tab switch → animate; label/count change → snap (no animation).
  useLayoutEffect(() => {
    const switched = lastKey.current !== activeKey;
    lastKey.current = activeKey;
    measure(switched && pillRef.current != null);
    // Keep the active tab in view when the bar is scrollable on narrow screens.
    if (switched) {
      const el = btnRefs.current[activeKey];
      const track = trackRef.current;
      if (el && track && track.scrollWidth > track.clientWidth) {
        const l = el.offsetLeft, r = l + el.offsetWidth;
        if (l < track.scrollLeft) track.scrollTo({ left: l - 8, behavior: 'smooth' });
        else if (r > track.scrollLeft + track.clientWidth) track.scrollTo({ left: r - track.clientWidth + 8, behavior: 'smooth' });
      }
    }
  }, [activeKey, sig]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fonts loading / container resizing can shift tab widths — re-measure without animating.
  useEffect(() => {
    const onResize = () => measure(false);
    window.addEventListener('resize', onResize);
    const ro = typeof ResizeObserver !== 'undefined' && trackRef.current ? new ResizeObserver(onResize) : null;
    if (ro) ro.observe(trackRef.current);
    if (document.fonts?.ready) document.fonts.ready.then(onResize).catch(() => {});
    return () => { window.removeEventListener('resize', onResize); if (ro) ro.disconnect(); };
  }, [activeKey, sig]); // eslint-disable-line react-hooks/exhaustive-deps

  const transition = animate
    ? `left ${DURATION}ms ${EASE}, width ${DURATION}ms ${EASE}, background-color ${DURATION}ms ${EASE}`
    : 'none';

  return (
    <div ref={trackRef} role="tablist"
      style={{ position:'relative', display:'flex', alignItems:'center', gap:4, overflowX:'auto', scrollbarWidth:'none', msOverflowStyle:'none' }}>
      {/* Sliding pill — sits behind the tab labels */}
      {pill && (
        <span aria-hidden="true" style={{
          position:'absolute', left: pill.left, top: pill.top, width: pill.width, height: pill.height,
          borderRadius: 9, background: pillColor, transition, pointerEvents:'none',
          boxShadow: `0 2px 8px ${pillColor === ALERT ? 'rgba(220,38,38,0.25)' : 'rgba(249,115,22,0.28)'}`,
          willChange: 'left, width',
        }} />
      )}
      {tabs.map(t => {
        const on = t.key === activeKey;
        const n = Number(t.count) || 0;
        const textColor = on ? '#fff' : (t.alert ? ALERT : T.text3);
        return (
          <button key={t.key} ref={el => { btnRefs.current[t.key] = el; }} type="button" role="tab" aria-selected={on}
            onClick={() => { if (!on) onChange(t.key); }}
            style={{ position:'relative', zIndex:1, display:'flex', alignItems:'center', gap:7, padding:'7px 14px', borderRadius:9,
              border:'none', cursor: on ? 'default' : 'pointer', whiteSpace:'nowrap', flexShrink:0,
              background: !on && hoverKey === t.key ? (darkMode ? 'rgba(148,163,184,0.10)' : '#f1f5f9') : 'transparent',
              fontSize:12.5, fontWeight: on ? 600 : 500, color: textColor,
              transition: `color ${DURATION}ms ${EASE}` }}
            onMouseEnter={() => setHoverKey(t.key)}
            onMouseLeave={() => setHoverKey(k => (k === t.key ? null : k))}>
            {t.label}
            {t.count != null && (
              <span style={{ fontSize:11, fontWeight:600, padding:'1px 7px', borderRadius:6, minWidth:10, textAlign:'center',
                background: on ? 'rgba(255,255,255,0.25)' : (t.alert && n ? (darkMode ? 'rgba(239,68,68,0.15)' : '#fef2f2') : T.bg3),
                color: on ? '#fff' : (t.alert && n ? ALERT : (n ? T.text2 : T.text3)),
                transition: `background-color ${DURATION}ms ${EASE}, color ${DURATION}ms ${EASE}` }}>
                {t.count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
